<script setup lang="ts">
// Before the event (the machine panel's Check tab): the health report, what the node
// knows in the order to fix it, and the self-test, what it can do,
// measured (healthreport.go, selftest.go).
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, ApiError, type HealthReport, type SelfTest } from '../../api'
import { selfTestVerdict } from '../../selftest'
import { useNode } from '../../store'
import { dateTime } from '../../util'

const node = useNode()
const report = ref<HealthReport | null>(null)
const checking = ref(false)
const test = ref<SelfTest | null>(null)
const refused = ref('') // why the self-test did not start: something runs
const memory = ref(false) // with root's memory test (2 GB, minutes: about 18 in a container)
let timer = 0

async function check() {
    checking.value = true
    try {
        report.value = await api.healthReport()
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        checking.value = false
    }
}
// shown: what needs a look first, the rest folded into a count
const problems = computed(() => report.value?.checks.filter((c) => c.state === 'fail' || c.state === 'warn') ?? [])
const fine = computed(() => report.value?.checks.filter((c) => c.state === 'ok').length ?? 0)

async function loadTest() {
    const was = test.value?.running
    try {
        test.value = await api.selfTest()
    } catch {
        /* an older node */
    }
    clearTimeout(timer)
    if (test.value?.running) timer = window.setTimeout(loadTest, 1500)
    else if (was && report.value) void check() // the report's self-test line follows it
}
async function runTest(force = false) {
    refused.value = ''
    try {
        test.value = await api.startSelfTest(force, memory.value)
        timer = window.setTimeout(loadTest, 1500)
    } catch (e) {
        if (e instanceof ApiError && e.status === 409 && !force && !e.message.includes('a self-test is running'))
            refused.value = e.message
        else node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
// cancel: the steps so far kept, the rest cancelled; a memory test stops
// within seconds (root kills memtester)
async function cancelTest() {
    try {
        test.value = await api.cancelSelfTest()
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
    void loadTest()
}
onMounted(loadTest)
onUnmounted(() => clearTimeout(timer))
const verdict = computed(() => (test.value ? selfTestVerdict(test.value) : ''))

const mark = (s: string) =>
    ({ pass: '✓', ok: '✓', fail: '✕', warn: '!', skip: '–', cancelled: '–', running: '…', waiting: '·' })[s] ?? s
const tone = (s: string) =>
    s === 'fail' ? 'text-bad' : s === 'warn' ? 'text-warn' : s === 'pass' || s === 'ok' ? 'text-ok' : 'text-fg-3'
</script>

<template>
    <!-- a box with its title on top and its actions as buttons in a row of their own -->
    <div class="rounded-md border border-line" data-test="event-check">
        <div class="border-b border-line bg-bg-2 px-2.5 py-1.5 subhead">Before the event</div>
        <div class="flex flex-wrap items-center gap-2 px-2.5 py-2">
            <button class="text-xs" :disabled="checking" data-test="health-check" @click="check">
                Check before the event
            </button>
            <button
                class="text-xs"
                :disabled="test?.running"
                title="About a minute of load: x264 and NVENC speed, disk writes, shared memory, DeckLink devices, the clock"
                data-test="self-test-run"
                @click="runTest()"
            >
                {{ test?.running ? 'Self-test running…' : 'Run self-test' }}
            </button>
            <button
                v-if="test?.running"
                class="text-xs"
                :disabled="test.cancelled"
                title="Stop it: the steps so far are kept, the rest cancelled; a memory test stops within seconds"
                data-test="self-test-cancel"
                @click="cancelTest"
            >
                {{ test.cancelled ? 'Cancelling…' : 'Cancel' }}
            </button>
            <label
                class="m-0 inline-flex items-center gap-1.5 text-xs text-fg-2"
                title="Root's memtester over 2 GB: minutes on a real machine, its step says how far it is and Cancel stops it; not while encoding"
                ><input v-model="memory" type="checkbox" data-test="self-test-memory" /> with memory test</label
            >
        </div>
        <div v-if="refused" class="flex flex-wrap items-center gap-2 px-2.5 pb-2 text-xs text-warn" role="alert">
            <span class="min-w-0 flex-1">{{ refused }}</span>
            <button class="text-xs" @click="runTest(true)">Run anyway</button>
        </div>
        <div v-if="report" class="border-t border-line px-2.5 py-2 text-xs" data-test="health-report">
            <div :class="report.ready ? 'text-ok' : 'text-bad'" class="font-medium">
                {{ report.ready ? 'Ready' : 'Not ready' }}
                <span class="font-normal text-fg-3">· {{ fine }} checks fine · {{ dateTime(report.at) }}</span>
            </div>
            <div v-for="(c, i) in problems" :key="i" class="py-0.5">
                <span :class="tone(c.state)" class="inline-block w-3 font-mono">{{ mark(c.state) }}</span>
                <span class="font-medium">{{ c.name }}</span
                ><span v-if="c.detail" class="text-fg-2"> — {{ c.detail }}</span>
            </div>
        </div>
        <div v-if="test?.steps.length" class="border-t border-line px-2.5 py-2 text-xs" data-test="self-test">
            <div class="text-fg-3">
                self-test
                <template v-if="test.running"
                    >{{ verdict === 'cancelling' ? 'cancelling, ' : '' }}running since
                    {{ dateTime(test.started) }}</template
                >
                <template v-else-if="test.finished">
                    <span :class="verdict === 'passed' ? 'text-ok' : verdict === 'failed' ? 'text-bad' : 'text-warn'">{{
                        verdict
                    }}</span>
                    {{ dateTime(test.finished) }}<template v-if="test.forced"> (run while encoding)</template>
                </template>
            </div>
            <div v-for="s in test.steps" :key="s.name" class="py-0.5">
                <span :class="tone(s.state)" class="inline-block w-3 font-mono">{{ mark(s.state) }}</span>
                {{ s.name
                }}<span v-if="s.unit && (s.state === 'pass' || s.state === 'fail')" class="text-fg-2 tabular-nums">
                    · {{ s.value.toFixed(1) }}{{ s.unit }}</span
                ><span v-if="s.detail || s.state === 'cancelled'" class="text-fg-2">
                    — {{ s.detail || 'cancelled' }}</span
                >
            </div>
        </div>
    </div>
</template>
